import {
  BaseFormElementData,
  BaseFormElementModel,
  BaseFormElementSettingsData,
  BaseFormElementState
} from '@/components/addons/registration/BaseFormElementModel';
import { addDays, isValid } from 'date-fns';

export interface FormElementDateSettingsData extends BaseFormElementSettingsData {
  date_field_type: 'input' | 'calendar';
  field_format: 'Y-m-d' | 'd-m-Y' | 'm-d-Y' | 'Y.m.d' | 'd.m.Y' | 'm.d.Y' | 'Y/m/d' | 'd/m/Y' | 'm/d/Y';
  time_enabled?: string;
  time_format12?: string;
  time_min?: string;
  time_max?: string;
  minage?: string;
  mindate?: string;
  maxdate?: string;
  disable_dates: string;
  disabled_dates_values: string[];
  dates_from_tomorrow?: string;
}

export interface FormElementDateData extends BaseFormElementData {
  settings?: FormElementDateSettingsData;
}

export interface FormElementDateState extends BaseFormElementState<string> {
  dateFieldType: 'input' | 'calendar';
  fieldFormat: 'Y-m-d' | 'd-m-Y' | 'm-d-Y' | 'Y.m.d' | 'd.m.Y' | 'm.d.Y' | 'Y/m/d' | 'd/m/Y' | 'm/d/Y';
  time: {
    enabled: boolean;
    format12?: string;
    min?: string;
    max?: string;
  };
  validationMinAge?: number;
  validationMinDate?: string;
  validationMaxDate?: string;
  maskType?: boolean;
  mask: string;
  disableCookie: boolean;
  disableDates: boolean;
  disabledDatesValues: string[];
  datesFromTomorrow?: boolean;
  config: {
    altFormat: string;
    timeformat: string;
  };
}

export class FormElementDateModel extends BaseFormElementModel<string, FormElementDateData, FormElementDateState> {
  parseFormElement(data: FormElementDateData) {
    this.state.dateFieldType = data.settings?.date_field_type || 'calendar';
    this.state.fieldFormat = data.settings?.field_format || 'Y-m-d';
    this.state.time = this.state.time ?? {};
    this.state.time.enabled = data.settings?.time_enabled === '1';
    this.state.time.format12 = data.settings?.time_format12;
    this.state.time.min = data.settings?.time_min;
    this.state.time.max = data.settings?.time_max;
    this.state.validationMinAge = data.settings?.minage ? parseInt(data.settings.minage, 10) : undefined;
    this.state.validationMinDate = data.settings?.mindate;
    this.state.validationMaxDate = data.settings?.maxdate;
    this.state.disableDates = data.settings?.disable_dates === '1';
    this.state.disabledDatesValues = data.settings?.disabled_dates_values || [];
    this.state.datesFromTomorrow = data.settings?.dates_from_tomorrow === '1';
    this.state.maskType = false;
    this.state.mask = '####-##-##';
    if (this.state.dateFieldType && this.state.dateFieldType === 'input') {
      this.state.maskType = true;

      if (this.state.fieldFormat) {
        this.state.mask = this.state.fieldFormat.replace('Y', '####').replace('d', '##').replace('m', '##');
      }
    }

    if (this.state.datesFromTomorrow) {
      this.state.validationMinDate = addDays(new Date(), 1).toISOString();
    }

    /**
     * Convert string date into something flatPickr can understand despite different this.state.fieldFormat
     */
    if (this.state.value && this.state.value.length > 0) {
      const date = new Date(Date.parse(this.state.value));
      if (isValid(date)) {
        this.state.value = date.toISOString();
      }
    }
  }

  getInitialValue(): string | undefined {
    return this.getInitialStringValue();
  }

  getStringifiedValue(): string | undefined {
    return this.state.value && this.state.value !== '' ? this.state.value : undefined;
  }

  parseStringValue(value: string): string | undefined {
    return value;
  }

  getSerializedPostValue(): string {
    return this.state.value ?? '';
  }

  getSerializedCookieValue(): string {
    return this.state.value ?? '';
  }

  getValidationRules(): string[] {
    const rules = super.getValidationRules();

    if (this.state.validationMinDate) {
      rules.push(`min_date:${this.state.validationMinDate},${this.state.fieldFormat}`);
    }

    if (this.state.validationMaxDate) {
      rules.push(`max_date:${this.state.validationMaxDate},${this.state.fieldFormat}`);
    }

    if (this.state.required && this.state.maskType) {
      rules.push('is_mask_date:' + this.state.fieldFormat);
    }

    return rules;
  }

  authorSignature(): string {
    return 'Nicky Christensen';
  }
}
